import { Intent } from '@blueprintjs/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../lib/queryKeys';
import { Notifications } from '../../notifications/components/Notifications';
import { observableToPromise } from '../../shared/observableToPromise';
import { patchAccountSubscription } from '../api';
import type { Account, Subscription } from '../model';

export function useUpdateStripeSubscriptionId(account: Account | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscription,
      stripeSubscriptionId,
    }: {
      subscription: Subscription;
      stripeSubscriptionId: string;
    }) => {
      if (!account) {
        throw new Error('Account not found');
      }
      const subscriptionIdValue = stripeSubscriptionId === '' ? undefined : stripeSubscriptionId;
      return observableToPromise(
        patchAccountSubscription(account, subscription, {
          ...subscription,
          stripeSubscriptionId: subscriptionIdValue,
        }),
      );
    },
    onSuccess: () => {
      if (!account) {
        return;
      }
      // Invalidate to refetch the latest subscription from the server
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.subscription(account._id),
      });
      Notifications.show({
        intent: Intent.SUCCESS,
        message: 'Updated stripe subscription ID successfully',
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string | { [key: string]: string } };
      const message =
        err.message && typeof err.message === 'object'
          ? (err.message as { [key: string]: string }).message
          : err.message || 'Unknown error';
      Notifications.show({
        intent: Intent.DANGER,
        message: `Failed to update stripe subscription ID: ${message}`,
      });
    },
  });
}

export function useUpdateOrbSubscriptionId(account: Account | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscription,
      orbSubscriptionId,
    }: {
      subscription: Subscription;
      orbSubscriptionId: string;
    }) => {
      if (!account) {
        throw new Error('Account not found');
      }
      const subscriptionIdValue = orbSubscriptionId === '' ? undefined : orbSubscriptionId;
      return observableToPromise(
        patchAccountSubscription(account, subscription, {
          ...subscription,
          orbSubscriptionId: subscriptionIdValue,
        }),
      );
    },
    onSuccess: () => {
      if (!account) {
        return;
      }
      // Invalidate to refetch the latest subscription from the server
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.subscription(account._id),
      });
      Notifications.show({
        intent: Intent.SUCCESS,
        message: 'Updated orb subscription ID successfully',
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string | { [key: string]: string } };
      const message =
        err.message && typeof err.message === 'object'
          ? (err.message as { [key: string]: string }).message
          : err.message || 'Unknown error';
      Notifications.show({
        intent: Intent.DANGER,
        message: `Failed to update orb subscription ID: ${message}`,
      });
    },
  });
}

export function useClearPendingSubscriptionUpdate(account: Account | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subscription }: { subscription: Subscription }) => {
      if (!account) {
        throw new Error('Account not found');
      }
      return observableToPromise(
        patchAccountSubscription(account, subscription, {
          ...subscription,
          pendingUpdate: false,
        }),
      );
    },
    onSuccess: () => {
      if (!account) {
        return;
      }
      // Invalidate to refetch the latest subscription from the server
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.subscription(account._id),
      });
      Notifications.show({
        intent: Intent.SUCCESS,
        message: 'Cleared pendingUpdate successfully',
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string | { [key: string]: string } };
      const message =
        err.message && typeof err.message === 'object'
          ? (err.message as { [key: string]: string }).message
          : err.message || 'Unknown error';
      Notifications.show({
        intent: Intent.DANGER,
        message: `Failed to clear pendingUpdate: ${message}`,
      });
    },
  });
}
